import clsx from 'clsx';
import {
  type ReactEventHandler,
  type ReactNode,
  forwardRef,
  ElementType,
  type ReactElement,
} from 'react';

import { avatarGroupItemWrapperCSS } from './AvatarGroup.sty.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import { Menu } from '../../navigation/menu/Menu.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';
import type { AvatarOwnProps } from '../avatar/Avatar.js';
import { Avatar } from '../avatar/Avatar.js';

//#region Typings
/**
 * Accepted properties for the Avatar Item
 * @public
 */
export interface AvatarGroupItemOwnProps extends AvatarOwnProps {
  /**
   * The text that is shown in the tooltip when hovering over an AvatarGroup.Item.
   */
  tooltipText: ReactNode;
}

// Merge own props with others inherited from the underlying element type
/**
 * @public
 */
export type AvatarGroupItemProps<E extends ElementType> =
  PolymorphicComponentProps<E, AvatarGroupItemOwnProps>;

//#endregion

/**
 * AvatarGroup Item component
 * @public
 */
export const Item: <E extends ElementType = 'div'>(
  props: AvatarGroupItemProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: AvatarGroupItemProps<E>,
    ref: typeof props.ref,
  ) => {
    const {
      size = 'default',
      abbreviation,
      disabled = false,
      tooltipText,
      as,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: AvatarGroupItemProps<E> = props;
    const isCollapsed = remainingProps.isCollapsed;

    return !isCollapsed ? (
      <div
        className={clsx(consumerClassName, avatarGroupItemWrapperCSS)}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        <Tooltip
          text={tooltipText}
          fallbackPlacements={[
            'top',
            'bottom',
            'top-start',
            'top-end',
            'bottom-start',
            'bottom-end',
            'right',
            'left',
            'right-start',
            'right-end',
            'left-start',
            'left-end',
          ]}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
        >
          <Avatar
            abbreviation={abbreviation}
            ref={ref}
            size={size}
            as={as as ElementType}
            className="rovingFocusElement"
            disabled={disabled}
            {...remainingProps}
          />
        </Tooltip>
      </div>
    ) : (
      <Menu.Item
        data-testid={dataTestId}
        onSelect={(() => null) as ReactEventHandler}
        className={clsx('rovingFocusElement', consumerClassName)}
        as={as as ElementType}
        disabled={disabled}
        style={consumerStyle}
        {...remainingProps}
      >
        <Avatar abbreviation={abbreviation} size="small" disabled={disabled}>
          <Avatar.Label>{tooltipText}</Avatar.Label>
        </Avatar>
      </Menu.Item>
    );
  },
);

(Item as typeof Item & { displayName: string }).displayName =
  'AvatarGroup.Item';
